import PropTypes from 'prop-types';
import { translationPropType } from '@eventbrite/i18n';
import { COLORS_PROP_TYPE } from '@eventbrite/eds-color';
import { ICON_TYPE_PROP_TYPE } from '@eventbrite/eds-icon';

export const IMAGE_PROP_TYPES = {
    /*
    Path to image, e.g. 'django/images/assortments/essentials.png'
    */
    imagePath: PropTypes.string,
    /*
    Description of image to be used for accessibility
    */
    altDescription: translationPropType,
    /*
    Content to display inside image, if image should be used as a background
    */
    children: PropTypes.node,
    /*
    type of illustration, if passed, will render Illustration instead of image
    */
    illustration: PropTypes.string,
    /*
    width of illustration
    */
    width: PropTypes.number,
    /*
    height of illustration
    */
    height: PropTypes.number,
    /*
    Position of background image
    */
    bgPosition: PropTypes.string,
    /*
    Size of background image
    */
    bgSize: PropTypes.string,
    /*
    resizes image to be round
    */
    isRound: PropTypes.bool,
};

export const TEMPLATE_PROP_TYPES = {
    /*
    Content to display alongside image
    */
    children: PropTypes.node,
    /*
    Whether items should be reversed from right to left
    */
    reverseHorizontal: PropTypes.bool,
    /*
    Whether items should be reversed from bottom to top
    */
    reverseVertical: PropTypes.bool,
    /*
    Whether the children should be shown only on mobile screens
    */
    showOnlySmallScreen: PropTypes.bool,
    /*
    Whether the template has padding or not
    */
    hasPadding: PropTypes.bool,
};

export const TEMPLATE_PROPS = Object.keys(TEMPLATE_PROP_TYPES);

export const HEADING_PROP_TYPES = {
    /*
    Main title
    */
    heading: translationPropType,
    /*
    Secondary title
    */
    secondaryHeading: translationPropType,
    /*
    Description to show in smaller font
    */
    description: translationPropType,
    /*
    Optional image to display below the heading
    */
    imagePath: PropTypes.string,
    /*
    Use a smaller font for main heading
    */
    useSmallFont: PropTypes.bool,
    /*
    Align center on small screens
    */
    alignCenter: PropTypes.bool,
    /*
    Add extra vertical padding
    */
    additionalPadding: PropTypes.bool,
    /*
    Optional Background color accepts eds format i.e. grey-900
    */
    bgColor: COLORS_PROP_TYPE,
    /*
    Optional heading color accepts eds format i.e. grey-90
    */
    headingColor: COLORS_PROP_TYPE,
    /*
    Optional text color accepts eds format i.e. grey-90
    */
    textColor: COLORS_PROP_TYPE,
    /*
    Boolean value that makes the heading smaller and bolder
    */
    useSmallerMessaging: PropTypes.bool,
    /*
    Boolean value that removes vertical padding
    */
    removeVerticalPadding: PropTypes.bool,
    /*
    Boolean value that removed horizontal padding
    */
    removeHorizontalPadding: PropTypes.bool,
};

export const HEADING_PROPS = Object.keys(HEADING_PROP_TYPES);

export const FEATURES_PROP_TYPES = {
    /*
    Group of features to show
    */
    features: PropTypes.arrayOf(
        PropTypes.shape({
            heading: translationPropType.isRequired,
            description: translationPropType.isRequired,
            iconType: ICON_TYPE_PROP_TYPE.isRequired,
        }),
    ),
};

export const TESTIMONIALS_PROP_TYPES = {
    /*
    Group of testimonials to show
    */
    testimonials: PropTypes.arrayOf(
        PropTypes.shape({
            imagePath: PropTypes.string.isRequired,
            quote: translationPropType.isRequired,
            byline: translationPropType.isRequired,
        }),
    ),
};

export const PRICING_PROP_TYPES = {
    /*
    Price of package
    */
    price: translationPropType,
    /*
    Description of price
    */
    priceDescription: translationPropType,
    /*
    Per paid ticket text to use when there is no fine print
    */
    perPaidTicketText: translationPropType,
    /*
    Fixed height to be aligned with other text content
    */
    fixedHeight: PropTypes.bool,
    /*
    Show asterisk on pricing
    */
    finePrint: translationPropType,
};

export const PRICING_PROPS = Object.keys(PRICING_PROP_TYPES);

export const FEATURES_COMPARISON_PROP_TYPES = {
    /*
    Primary page heading
    */
    primaryHeading: translationPropType.isRequired,
    /*
    Selected package, if the user navigated from a plan page
    */
    selectedPackage: PropTypes.string,
    /*
    Kill switch for displaying the max fee per ticket
    */
    showMaxFee: PropTypes.bool,
};

export const PROP_TYPES_CRUMBS = PropTypes.arrayOf(
    PropTypes.shape({
        /*
    Path of breadcrumb link
    */
        path: PropTypes.string,
        /*
    Breadcrumb label
    */
        label: translationPropType,
    }),
);
